import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  
} from 'recharts';
import { startOfDay, endOfDay } from 'date-fns';
import { useErrorSetter } from '../hooks/useUiMessages';
import { fetchDetailedEngagement, fetchDailyTrends, fetchRealtimeAnalytics, fetchAnalyticsSummary, fetchProductFunnels, fetchTopActiveUsers } from './analyticsApi';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f'];

const UserEngagement = ({ dateRange }) => {
  const setError = useErrorSetter();
  const [loading, setLoading] = useState(true);
  const [engagementData, setEngagementData] = useState({
    engagementMetrics: [], // daily trends: sessions, users, messages
    messageFrequency: [],  // hourly activity (last 24h)
    userRetention: [],
    featureUsage: [],      // service usage from product funnels
  topUsers: [],          // most active users (top 10)
    bounceRate: [],
    summary: {
      averageSessionDuration: 0,
      averageMessagesPerSession: 0,
      userRetentionRate: 0,
      bounceRate: 0,
      totalActiveUsers: 0,
      deepEngagementRate: 0
    }
  });

  const fetchEngagementData = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
      if (!token) {
        // unauthenticated: empty safe state
        setEngagementData({ 
          engagementMetrics: [], 
          messageFrequency: [], 
          userRetention: [], 
          featureUsage: [], 
          bounceRate: [], 
          summary: { 
            averageSessionDuration: 0, 
            averageMessagesPerSession: 0, 
            userRetentionRate: 0, 
            bounceRate: 0, 
            totalActiveUsers: 0, 
            deepEngagementRate: 0 
          } 
        });
        return;
      }

      const startDate = startOfDay(dateRange.start).toISOString();
      const endDate = endOfDay(dateRange.end).toISOString();

      // Fetch required datasets in parallel
      const [detailed, summaryPayload, daily, realtime, funnels, topUsersRes] = await Promise.all([
        fetchDetailedEngagement(startDate, endDate),
        fetchAnalyticsSummary(startDate, endDate, 'all'),
        fetchDailyTrends(startDate, endDate),
        fetchRealtimeAnalytics(),
        fetchProductFunnels(startDate, endDate),
        fetchTopActiveUsers(startDate, endDate)
      ]);

      // Derive summary using available fields
      const avgSessionDuration = detailed?.averageSessionDuration || 0;
      const avgMessages = detailed?.averageMessagesPerSession || 0;
      const bounceRateVal = detailed?.bounceRate || 0;
      const totalActiveUsers = (summaryPayload?.summary?.authUsers || 0) + (summaryPayload?.summary?.unauthUsers || 0);

      // Daily trends mapped for chart
      const dailyEngagement = Array.isArray(daily)
        ? daily.map(d => ({
            date: d._id || d.date || '',
            sessions: d.sessions || 0,
            users: d.users || 0,
            messages: d.messages || 0
          }))
        : [];

      // Hourly activity (last 24h)
      const messageFrequency = Array.isArray(realtime?.hourlyBreakdown)
        ? realtime.hourlyBreakdown.map(h => ({ hour: (h._id?.hour ?? h.hour ?? 0), messages: h.count || 0 }))
        : [];

      // Feature/service usage from product funnels
      const featureUsage = (() => {
        const r = funnels?.resume || {};
        const c = funnels?.coverLetter || {};
        const i = funnels?.interview || {};
        const j = funnels?.jobs || {};
        return [
          { feature: 'Resumes Created', usage: r.created || 0 },
          { feature: 'Resumes Downloaded', usage: r.downloaded || 0 },
          { feature: 'Cover Letters Created', usage: c.created || 0 },
          { feature: 'Interviews Generated', usage: i.generated || 0 },
          { feature: 'Jobs Fetched', usage: j.completed || 0 }
        ];
      })();

      setEngagementData({
        engagementMetrics: dailyEngagement,
        messageFrequency,
        userRetention: Array.isArray(detailed?.retention) ? detailed.retention.map(r => ({
          week: r.cohort || r.week || '',
          newUsers: r.users || 0,
          returnedUsers: r.retainedW1 || r.returnedUsers || 0,
          retentionRate: typeof r.rateW1 === 'number' ? r.rateW1 : (r.retentionRate || 0)
        })) : [],
        featureUsage,
        topUsers: Array.isArray(topUsersRes?.topUsers) ? topUsersRes.topUsers.map(u => ({
          name: u.name || 'Guest',
          sessions: u.sessions || 0,
          messages: u.messages || 0,
          totalTime: u.totalTimeSec || 0,
          lastActive: u.lastActive ? new Date(u.lastActive).toLocaleString() : ''
        })) : [],
        bounceRate: [],
        summary: {
          averageSessionDuration: avgSessionDuration,
          averageMessagesPerSession: avgMessages,
          userRetentionRate: typeof detailed?.userRetentionRate === 'number' ? detailed.userRetentionRate : 0,
          bounceRate: bounceRateVal,
          totalActiveUsers,
          deepEngagementRate: typeof detailed?.deepEngagementRate === 'number' ? detailed.deepEngagementRate : 0
        }
      });
    } catch (err) {
      console.error('Error fetching engagement data:', err);
      setError('Failed to load engagement metrics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, setError]);

  useEffect(() => {
    fetchEngagementData();
  }, [fetchEngagementData]);

  // Legacy processors removed (data now transformed inline in fetchEngagementData)

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading engagement data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className={styles.engagementContainer}>
      {/* Summary Cards */}
      <Grid container spacing={3} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Active Users
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatNumber(engagementData.summary.totalActiveUsers)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Avg. Session Duration
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {formatDuration(engagementData.summary.averageSessionDuration)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Messages per Session
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {engagementData.summary.averageMessagesPerSession.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                User Retention
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {engagementData.summary.userRetentionRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Bounce Rate
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {engagementData.summary.bounceRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                Deep Engagement
              </Typography>
              <Typography variant="h4" className={styles.metricValue}>
                {engagementData.summary.deepEngagementRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3} className={styles.chartsGrid}>
        {/* Daily Engagement Trends */}
        <Grid item xs={12} lg={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Daily Engagement Metrics
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={engagementData.engagementMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stackId="1"
                    stroke={COLORS[0]} 
                    fill={COLORS[0]}
                    name="Users"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sessions" 
                    stackId="1"
                    stroke={COLORS[1]} 
                    fill={COLORS[1]}
                    name="Sessions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

  {/* Removed Session Duration Distribution (no backend distribution yet) */}

        {/* Hourly Message Activity */}
        <Grid item xs={12} lg={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Hourly Message Activity
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={engagementData.messageFrequency}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="messages" fill={COLORS[2]} name="Messages" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Usage */}
        <Grid item xs={12} lg={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Feature Usage
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={engagementData.featureUsage} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="feature" type="category" width={80} />
                  <Tooltip />
                  <Bar dataKey="usage" fill={COLORS[3]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Most Active Users */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Most Active Users
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">Total Time</TableCell>
                      <TableCell align="right">Last Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagementData.topUsers.slice(0, 10).map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell align="right">{user.sessions}</TableCell>
                        <TableCell align="right">{user.messages}</TableCell>
                        <TableCell align="right">{formatDuration(user.totalTime)}</TableCell>
                        <TableCell align="right">{user.lastActive}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* User Retention */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                User Retention Analysis
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">New Users</TableCell>
                      <TableCell align="right">Returned</TableCell>
                      <TableCell align="right">Retention %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagementData.userRetention.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.week}</TableCell>
                        <TableCell align="right">{row.newUsers}</TableCell>
                        <TableCell align="right">{row.returnedUsers}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${row.retentionRate}%`}
                            color={parseFloat(row.retentionRate) > 30 ? 'success' : parseFloat(row.retentionRate) > 15 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

  {/* Removed Engagement Depth Over Time (no time-series depth breakdown yet) */}
      </Grid>
    </div>
  );
};

export default UserEngagement;
